import * as React from "react";
import { useSelector } from "react-redux";

import DiffText from "@/components/DiffText";
import { useTranslations } from "@/queries/useTranslations";
import { useVersions } from "@/queries/useVersions";
import { type IState, docsItemSelector } from "@/state";
import type { ILanguage } from "@/types";
import { useNavigate, useSearch } from "@tanstack/react-router";

interface IProps {
    version: string;
    id: string;
    lang: ILanguage;
}

function DocsDiff({ id, lang, version }: IProps) {
    const navigate = useNavigate({ from: "/$version" });
    const diffVersion = useSearch({ from: "/$version", select: (state) => state.since }) ?? "";
    const { data: versions = [] } = useVersions();
    const { data: translations } = useTranslations(version);
    const { data: diffTranslations } = useTranslations(diffVersion);
    const text = useSelector((state: IState) => docsItemSelector(state, version, id, translations!)[lang]);
    const diffText = useSelector((state: IState) =>
        diffVersion && diffTranslations ? docsItemSelector(state, diffVersion, id, diffTranslations!)[lang] : null,
    );

    const onChange = (event: React.FormEvent<HTMLSelectElement>) => {
        const value = event.currentTarget.value;
        navigate({
            to: ".",
            search: (prev) => ({ ...prev, since: value || undefined, page: undefined }),
        });
    };

    return (
        <>
            <select onChange={onChange} value={diffVersion}>
                <option value="" key="">
                    Select version
                </option>
                {versions
                    .filter((v) => v !== version)
                    .map((v) => (
                        <option value={v} key={v}>
                            {v}
                        </option>
                    ))}
            </select>
            <div>
                {!!diffText && (
                    <DiffText newText={text} oldText={diffText || ""} oldVersion={diffVersion} newVersion={version} />
                )}
            </div>
        </>
    );
}

export default React.memo(DocsDiff);
